import moment from 'moment'
import { useCallback, useState, useEffect } from 'react'
import {
  DATE_TIME_FORMAT,
  HOURS,
  MINUTES,
  MONTHDAYS,
  MONTHS,
  WEEKDAYS,
  initialValues
} from '../constants'
import { getCronExp } from './cronexp'

export const isInvalidByField = {
  jobType: ({ value, submitted }) => submitted && !value,
  mailTo: ({ submitted }) => submitted && false,
  cronStart: ({
    value,
    submitted,
    disabled,
    values: { cronEnd },
    isScheduledEditing
  }) => {
    if (disabled || !submitted) return
    const isGteToday = moment(value).startOf('day') >= moment().startOf('day')
    if (isScheduledEditing) {
      // In editing mode we only check if the dates are bigger than each other,with no checks on today's date
      if (cronEnd) {
        return moment(value).startOf('day') >= moment(cronEnd).startOf('day')
      } else return false
    }
    return submitted && !isGteToday
  },
  cronEnd: ({
    value,
    submitted,
    disabled,
    values: { cronStart },
    isScheduledEditing
  }) => {
    if (disabled || !submitted) return
    const isGteToday = moment(value).startOf('day') >= moment().startOf('day')
    const isGteStart =
      moment(value).startOf('day') >= moment(cronStart).startOf('day')
    if (isScheduledEditing) {
      // In editing mode we only check if the dates are bigger than each other,with no checks on today's date
      if (cronStart) {
        return !isGteStart
      } else return false
    }
    return submitted && (!isGteToday || !isGteStart)
  },
  monthdays: ({ value, disabled, isEditing }) => {
    if (disabled || !isEditing || value === '') return
    value.forEach(val => {
      if(val < 1 || val > 31)
        return false
    })
  },
  minutes: ({ value, disabled, isEditing }) => {
    if (disabled || !isEditing || value === '') return
    return value < 0 || value > 59
  },
  hours: ({ value, disabled, isEditing }) => {
    if (disabled || !isEditing || value === '') return
    return value < 0 || value > 23
  },
  months: ({ value, disabled, isEditing }) => {
    if (disabled || !isEditing || value === '') return
    value.forEach(val => {
      if(val > 12 || val < 1)
        return false
    })
  },
  weekdays: ({ value, disabled, isEditing }) => {
    if (disabled || !isEditing || value === '') return
    value.forEach(val => {
      if(val < 1 || val > 7)
        return false
    })
  },
  timezone: ({ value, submitted, disabled }) => {
    if (disabled || !submitted) return
    return submitted && !value
  },
  cronExp: ({ value, submitted, disabled }) => {
    if (disabled || !submitted) return
    return submitted && !value
  }
}

const getCronValuesByExp = (cronExp) => {
  const keys = ['minutes', 'hours', 'monthdays', 'months', 'weekdays']

  if (!cronExp) return keys.reduce((acc, inc) => ({ ...acc, [inc]: '' }), {})

  const cronExpValues = cronExp.split(' ')

  return keys.reduce((acc, key, i) => {
    const value = cronExpValues[i]
    if (value === '*') {
      return { ...acc, [key]: '' }
    }else if(value.split(',').length > 1){
      const arrayValue = value.split(',')
      return { ...acc, [key]: arrayValue }
    }else if(value.split('-').length > 1){
      const arrayValue = value.split('-')
      const values = []
      for (let i = arrayValue[0]; i <= arrayValue[1]; i++) {
        values.push(i.toString())
      }

      return { ...acc, [key]: values }
    }

    return { ...acc, [key]: cronExpValues[i] }

  }, {})
}

const processDefaults = (data) => {
  const { cronEnd, cronExp, cronStart, mails, timeZone, scenarioJobName } = data

  const { minutes, hours, months, weekdays, monthdays } =
    getCronValuesByExp(cronExp)

  return {
    jobType: scenarioJobName,
    mailTo: mails,
    cronStart,
    cronEnd,
    minutes,
    hours,
    months,
    weekdays,
    monthdays,
    timezone: timeZone,
    cronExp
  }
}

export const useCronScheduledForm = ({
  onSubmit,
  isScheduledEditing,
  defaultScheduledData
}) => {
  const [submitted, setSubmitted] = useState(false)

  const defaultValues = isScheduledEditing
    ? processDefaults(defaultScheduledData)
    : initialValues
  const [values, setValues] = useState(defaultValues)
  const [cronSettingsEnabled, enableCronSettings] = useState(
    isScheduledEditing ? defaultScheduledData.enabled : true
  )

  useEffect(() => {
    if (!cronSettingsEnabled) {
      setValues((values) => ({
        ...values,
        cronStart: null,
        cronEnd: null,
        monthdays: [],
        minutes: '',
        hours: '',
        months: [],
        weekdays: [],
        timezone: ''
      }))
    }
  }, [cronSettingsEnabled])

  const setFieldValue = (field, value) =>
    setValues((values) => ({
      ...values,
      [field]: value
    }))
  const onFormSubmit = useCallback(
    (e) => {
      e.preventDefault()
      setSubmitted(true)

      const invalidForm = Object.entries(values).find(([key, value]) =>
        isInvalidByField[key]({
          value,
          submitted: true,
          disabled: !cronSettingsEnabled,
          values,
          isScheduledEditing
        })
      )
      if (invalidForm) {
        console.error(
          `Error: Invalid fields in form: ${invalidForm?.join(',')}`
        )
        return
      }

      onSubmit({
        ...values,
        cronStart: values.cronStart
          ? moment(values.cronStart).format(DATE_TIME_FORMAT)
          : values.cronStart,
        cronEnd: values.cronEnd
          ? moment(values.cronEnd).format(DATE_TIME_FORMAT)
          : values.cronEnd,
        enabled: cronSettingsEnabled
      })
    },
    [values]
  )

  return {
    values,
    setFieldValue,
    submitted,
    onFormSubmit,
    cronSettingsEnabled,
    enableCronSettings
  }
}

export const useCronExp = ({
  minutes,
  hours,
  months,
  weekdays,
  monthdays,
  disabled,
  defaultCronExp,
  setFieldValue
}) => {
  const [cronExp, setCronExp] = useState(defaultCronExp)

  useEffect(() => {
    if (cronExp) {
      setFieldValue('cronExp', disabled ? null : cronExp)
    }
  }, [cronExp, disabled])

  useEffect(() => {
    setCronExp(getCronExp({ minutes, hours, months, weekdays, monthdays }))
  }, [minutes, hours, months, weekdays, monthdays, defaultCronExp])

  return { cronExp }
}

const initialEditingValues = {
  [MINUTES]: false,
  [HOURS]: false,
  [MONTHS]: false,
  [WEEKDAYS]: false,
  [MONTHDAYS]: false
}

export const useCronExpForm = ({
  disabled,
  minutes,
  hours,
  months,
  weekdays,
  monthdays,
  setFieldValue,
  defaultScheduledData
}) => {
  const [editingValues, setEditingValues] = useState(initialEditingValues)

  useEffect(() => {
    if (disabled) setEditingValues(initialEditingValues)
  }, [disabled])

  const { cronExp } = useCronExp({
    minutes,
    hours,
    months,
    weekdays,
    monthdays,
    defaultCronExp: defaultScheduledData?.cronExp,
    disabled,
    setFieldValue
  })

  const setValue = (field, event) => {
    setFieldValue(field, event.target.value)
    // Invalid message has to appear only when field editing is true for that field
    setEditingValues((prev) => ({ ...prev, [field]: true }))
  }

  const getHandleFieldChange = (field) => (event) => setValue(field, event)

  return {
    cronExp,
    editingValues,
    getHandleFieldChange,
    setValue
  }
}
